import { useState } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PinField, TextField } from '@speeki/global-ui-components';

import { InlineNotice } from '@components/atoms/InlineNotice/InlineNotice';

import { useOrganisationLabel } from '@utils/hooks/useOrganisationLabel';
import { isSameEmail, normaliseEmail } from '@utils/reporterEmail';

import { useReactiveVar } from '@apollo/client';
import {
    conversationDetailsVar,
    verifiedReporterEmailsVar,
} from '@boot/reactiveVariables';
import { ReporterEmailConfirmation } from '@domains/new-conversation/organisms/ReporterEmailConfirmation/ReporterEmailConfirmation';
import { AttachedField } from '@domains/new-conversation/organisms/ReporterEmailConfirmation/ReporterEmailConfirmation.styled';
import {
    ConversationText,
    ConversationTitle,
} from '@domains/new-conversation/pages/Conversation.styled';
import { ReportMailAndPinFormValues } from '@domains/new-conversation/pages/ReportSetPinPage/ReportSetPinPage';

import { FormWrapper, ShowPin } from './ReportMailAndPinForm.styled';

export const ReportMailAndPinForm = () => {
    const { t } = useTranslation();
    const organisationLabel = useOrganisationLabel();
    const {
        control,
        formState: { errors },
        register,
        setFocus,
        setValue,
    } = useFormContext<ReportMailAndPinFormValues>();

    const [isVisible, setIsVisible] = useState(false);

    const {
        field: { name, onChange },
    } = useController({ control, name: 'pin' });

    const updatesEmail = useWatch({ control, name: 'email' });
    const identityEmail = useReactiveVar(conversationDetailsVar)?.email;
    const isAlreadyDisclosed = isSameEmail(updatesEmail, identityEmail);

    const verifiedEmails = useReactiveVar(verifiedReporterEmailsVar);
    const isUpdatesEmailConfirmed =
        !!updatesEmail && verifiedEmails.includes(normaliseEmail(updatesEmail));

    /*
     * Submission is never held up by this address - an unconfirmed one is
     * simply discarded server-side. That silent discard is exactly why the
     * warning sits here, under the field it is about, for as long as a valid
     * address is typed but unconfirmed.
     */
    const isUpdatesEmailUnconfirmed =
        !!updatesEmail && !errors.email && !isUpdatesEmailConfirmed;

    return (
        <FormWrapper>
            <ConversationTitle>{t('REPORT_PIN_SET')}</ConversationTitle>
            <ConversationText>{t('REPORT_PIN_INFO')}</ConversationText>
            <PinField
                className="htmlToImageIgnore"
                error={!!errors.pin}
                id={name}
                message={t(errors.pin?.message?.toString() || '')}
                name={name}
                onChange={onChange}
                spacing={8}
                testId="jIr2Goq"
                type={isVisible ? 'number' : 'password'}
                xsSpacing={6}
            />
            <ShowPin
                isError={!!errors.pin?.message}
                onClick={() => setIsVisible((prevState) => !prevState)}
            >
                {t(isVisible ? 'HIDE_PIN' : 'SHOW_PIN')}
            </ShowPin>

            <ConversationTitle>{t('UPDATE_RECEIVE')}</ConversationTitle>
            <ConversationText>{t('UPDATE_EMAIL_INFO_1')}</ConversationText>
            {/* One control: the field, the confirmation slot on its edge and
             * the code entry under it all live inside AttachedField. The
             * helper line swaps once the address is confirmed - the privacy
             * rule and the discard rule share a single sentence before that. */}
            <AttachedField>
                <TextField
                    {...register('email')}
                    autoComplete={'off'}
                    className="htmlToImageIgnore"
                    error={!!errors.email?.message}
                    id={'email'}
                    label={t('REPORTER_UPDATES_EMAIL_LABEL_OPTIONAL')}
                    message={
                        typeof errors.email?.message === 'string'
                            ? t(errors?.email?.message.toString())
                            : isUpdatesEmailConfirmed
                              ? t('REPORTER_UPDATES_EMAIL_CONFIRMED_HELPER')
                              : t('REPORTER_UPDATES_EMAIL_PRIVACY')
                    }
                    placeholder={t('EMAIL_PLACEHOLDER_OPTIONAL')}
                    testId="u2g1VoG"
                />
                <ReporterEmailConfirmation
                    email={updatesEmail}
                    isEmailValid={!errors.email?.message}
                    onChangeAddress={() => setFocus('email')}
                    onRemoveAddress={() =>
                        setValue('email', '', {
                            shouldDirty: true,
                            shouldValidate: true,
                        })
                    }
                />
            </AttachedField>
            {isUpdatesEmailUnconfirmed && (
                <InlineNotice
                    testId="notice_updates-email-unconfirmed-submit"
                    variant={'warning'}
                >
                    {t('REPORTER_UPDATES_EMAIL_UNCONFIRMED_SUBMIT')}
                </InlineNotice>
            )}
            {isAlreadyDisclosed && (
                <InlineNotice
                    assertive
                    testId="notice_updates-email-already-disclosed"
                    variant={'warning'}
                >
                    {t('REPORTER_SAME_EMAIL_WARNING', {
                        organisation: organisationLabel,
                    })}
                </InlineNotice>
            )}
        </FormWrapper>
    );
};
